import { yupResolver } from '@hookform/resolvers/yup';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { InferType } from 'yup';
import { emergencyContactSchema } from '../../../../validation/clientProfile.schemas';

type EmergencyContactFormValues = InferType<typeof emergencyContactSchema>;

export interface EmergencyContactData {
  name: string;
  relationship: string;
  phone: string;
}

interface EmergencyContactSectionProps {
  data: EmergencyContactData;
  onSave: (data: EmergencyContactData) => void;
}

const RELATIONSHIPS = ['Family', 'Friend', 'Neighbor', 'Veterinarian', 'Other'];

const EmergencyContactSection: React.FC<EmergencyContactSectionProps> = ({ data, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EmergencyContactFormValues>({
    resolver: yupResolver(emergencyContactSchema),
    defaultValues: {
      emergencyContactName: data.name,
      emergencyContactRelationship: data.relationship,
      emergencyContactPhone: data.phone,
    },
  });

  const handleEdit = () => {
    reset({
      emergencyContactName: data.name,
      emergencyContactRelationship: data.relationship,
      emergencyContactPhone: data.phone,
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    reset();
    setIsEditing(false);
  };

  const onSubmit = (values: EmergencyContactFormValues) => {
    onSave({
      name: values.emergencyContactName ?? '',
      relationship: values.emergencyContactRelationship ?? '',
      phone: values.emergencyContactPhone ?? '',
    });
    setIsEditing(false);
    setSnackbarOpen(true);
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Emergency Contact
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            This person will be contacted if the sitter cannot reach you during a booking.
          </Typography>
        </Box>
        {!isEditing && (
          <IconButton onClick={handleEdit} size="small" sx={{ color: '#178B8E' }}>
            <EditIcon />
          </IconButton>
        )}
      </Box>

      {isEditing ? (
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="emergencyContactName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label="Contact Name"
                    fullWidth
                    size="small"
                    error={!!errors.emergencyContactName}
                    helperText={errors.emergencyContactName?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="emergencyContactRelationship"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!errors.emergencyContactRelationship}>
                    <InputLabel>Relationship</InputLabel>
                    <Select {...field} value={field.value ?? ''} label="Relationship">
                      {RELATIONSHIPS.map((r) => (
                        <MenuItem key={r} value={r}>
                          {r}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.emergencyContactRelationship && (
                      <FormHelperText>{errors.emergencyContactRelationship.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="emergencyContactPhone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label="Phone Number"
                    fullWidth
                    size="small"
                    error={!!errors.emergencyContactPhone}
                    helperText={errors.emergencyContactPhone?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button onClick={handleCancel} variant="outlined" sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ textTransform: 'none', backgroundColor: '#178B8E' }}
                >
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PersonIcon sx={{ color: '#178B8E', fontSize: 20 }} />
            <Typography variant="body1">{data.name || 'Not set'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <GroupIcon sx={{ color: '#178B8E', fontSize: 20 }} />
            <Typography variant="body1">{data.relationship || 'Not set'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PhoneIcon sx={{ color: '#178B8E', fontSize: 20 }} />
            <Typography variant="body1">{data.phone || 'Not set'}</Typography>
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
          Emergency contact updated!
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default EmergencyContactSection;
